// ---------------------------------------------------------------------------
// SideNav and LogoMark, ported from the design system (R9)
//
// <sdr-side-nav>: the console's persistent left navigation, with work counts
// and the Autopilot switch in its footer. On top the brand: the product in
// plain type with its endorsement (the NDI mark, else the text "by NDI"), and
// the workspace; since R6 (S10) the brand shows for a product or a workspace,
// the product's line only with a product. Then the items, grouped where
// neighbours share a `section` (a heading per group); then the footer.
//
//   - each item is a native button: Enter and Space press it, as in the
//     system, which has no key handler of its own; the chosen one is
//     aria-current="page". An item is chosen only with an id equal to the
//     value (S10): one without an id never is, also while nothing is chosen;
//   - a count is set in the locale (num), with a dot before it when the item
//     has a countTone;
//   - as in the system's useControlled, `value` makes it controlled: a click
//     reports the item's id through onChange and the nav shows what it is
//     given; without `value` it starts at `defaultValue` (read once, at the
//     first render, like React's useState) and keeps its own choice. Nothing
//     is chosen when neither is set. A click on the chosen item reports too;
//   - each item keeps its button across renders by React's key (its id), so
//     a focused item stays focused when items are added or removed before it;
//     a reorder moves the focused button, and a moved node loses focus, so,
//     like React after each commit, the nav gives focus back to the element
//     inside it that had focus before the render, if it is still there.
//
// logoMark(): a transparent logo file used as a CSS mask, so it draws in the
// text colour around it — a span with role="img" and the mark's name, never
// an <img>; the system's LogoMark, which SideNav uses for endorsementMark.
//
// Renames on the element (the system's names would shadow HTMLElement's or
// read as the element's own): className is navClass, label (the nav's
// accessible name) is navLabel. logoMark() keeps the system's names.
//
// The footer goes through node() from helpers.ts: React's rule that true and
// false render nothing.
// ---------------------------------------------------------------------------

import { html, LitElement, type PropertyValues, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';

import type { Tone } from './badge.js';
import { cx, noBox, node, num, present } from './helpers.js';
import { type IconName, icon } from './icons.js';

export interface LogoMarkSource {
  /** A transparent logo file (PNG or SVG). */
  src: string;
  /** The mark's name for a screen reader ("NDI"). */
  label: string;
  /** Width over height; 1 when not given. */
  ratio: number;
}

/** A logo drawn in currentColor through a mask; its height comes from the stylesheet. */
export function logoMark(m: LogoMarkSource): TemplateResult {
  const img = `url("${m.src}")`;
  return html`<span
    class="sdr-logo-mark"
    role="img"
    aria-label=${ifDefined(m.label)}
    style=${`aspect-ratio:${String(m.ratio || 1)};-webkit-mask-image:${img};mask-image:${img}`}
  ></span>`;
}

export interface NavItem {
  id: string;
  label: string;
  icon?: IconName;
  count?: number;
  /** Only when the item needs a person: approvals waiting (info), an account in trouble (caution). */
  countTone?: Tone;
  section?: string;
}

interface NavGroup {
  section: string | undefined;
  items: NavItem[];
}

/** Neighbouring items with the same section, in order; a section that comes back starts a new group. */
function groupsOf(items: NavItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let cur: NavGroup | null = null;
  for (const it of items) {
    if (!cur || it.section !== cur.section) {
      cur = { section: it.section, items: [] };
      groups.push(cur);
    }
    cur.items.push(it);
  }
  return groups;
}

export class SdrSideNav extends LitElement {
  static override properties = {
    items: { attribute: false },
    product: { attribute: false },
    endorsement: { attribute: false },
    endorsementMark: { attribute: false },
    workspace: { attribute: false },
    value: { attribute: false },
    defaultValue: { attribute: false },
    onChange: { attribute: false },
    footer: { attribute: false },
    navLabel: { attribute: false },
    navClass: { attribute: false },
    own: { state: true },
  };

  declare items: NavItem[];
  /** Set in plain type; there is no logo. */
  declare product: string | undefined;
  /** The text fallback, always "by NDI". */
  declare endorsement: string | undefined;
  /** The NDI mark from the Logos group: { src, label: 'NDI', ratio: 2.5677 }. */
  declare endorsementMark: LogoMarkSource | undefined;
  declare workspace: string | undefined;
  declare value: string | undefined;
  declare defaultValue: string | undefined;
  declare onChange: ((id: string) => void) | undefined;
  declare footer: unknown;
  /** The nav's accessible name; "Main" by default (the system's label). */
  declare navLabel: string | undefined;
  /** The system's className. */
  declare navClass: string | undefined;
  protected declare own: string | undefined;
  /** What had focus inside the nav when a render began. */
  private focusedBefore: HTMLElement | null = null;

  constructor() {
    super();
    this.items = [];
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
  }

  protected override willUpdate(_changed: PropertyValues<this>) {
    // React's useState(initial): the default is read once, at the first render.
    if (!this.hasUpdated) this.own = this.defaultValue;
    const at = document.activeElement;
    this.focusedBefore = at instanceof HTMLElement && this.contains(at) ? at : null;
  }

  protected override updated(_changed: PropertyValues<this>) {
    // React's restoreSelection: the element that had focus gets it back if it is still in the page.
    const was = this.focusedBefore;
    this.focusedBefore = null;
    if (was?.isConnected && document.activeElement !== was) was.focus({ preventScroll: true });
  }

  /** The chosen item's id: `value` when given, else the nav's own choice. */
  get current(): string | undefined {
    return this.value !== undefined ? this.value : this.own;
  }

  private pick(id: string) {
    if (this.value === undefined) this.own = id;
    this.onChange?.(id);
  }

  private brand() {
    if (!this.product && !this.workspace) return null;
    const endorsement = this.endorsementMark
      ? html`<span class="sdr-nav__endorsement"> by ${logoMark(this.endorsementMark)}</span>`
      : this.endorsement
        ? html`<span class="sdr-nav__endorsement">${` ${this.endorsement}`}</span>`
        : null;
    return html`<div class="sdr-nav__brand">
      ${this.product ? html`<span class="sdr-nav__product">${this.product}${endorsement}</span>` : null}
      ${this.workspace ? html`<span class="sdr-nav__workspace">${this.workspace}</span>` : null}
    </div>`;
  }

  private item(it: NavItem, current: string | undefined) {
    // Every item needs its id: it is the key, and an item without one is never the current page.
    const on = it.id != null && it.id === current;
    return html`<button
      type="button"
      class="sdr-nav__item"
      aria-current=${ifDefined(on ? 'page' : undefined)}
      @click=${() => this.pick(it.id)}
    >${it.icon ? icon({ name: it.icon, size: 16, className: 'sdr-nav__icon' }) : null}<span class="sdr-nav__label"
        >${it.label}</span
      >${
        it.count != null
          ? html`<span class=${cx('sdr-nav__count', it.countTone && `sdr-nav__count--${it.countTone}`)}
              >${num(it.count)}</span
            >`
          : null
      }</button>`;
  }

  override render() {
    const current = this.current;
    return html`<nav class=${cx('sdr-nav', this.navClass)} aria-label=${this.navLabel || 'Main'}>
      ${this.brand()}
      ${groupsOf(this.items || []).map(
        g => html`<div class="sdr-nav__group">
          ${g.section ? html`<div class="sdr-nav__section">${g.section}</div>` : null}
          ${repeat(
            g.items,
            it => it.id,
            it => this.item(it, current),
          )}
        </div>`,
      )}
      ${present(this.footer) ? html`<div class="sdr-nav__foot">${node(this.footer)}</div>` : null}
    </nav>`;
  }
}

if (!customElements.get('sdr-side-nav')) customElements.define('sdr-side-nav', SdrSideNav);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-side-nav': SdrSideNav;
  }
}
